import React, { useEffect, useState } from 'react';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { ReactPdf } from '../../../common/components/PdfViewer/ReactPdf';
import { useNotifications } from '../../../common/hooks/useNotifications';
import {
  CreateUpdateAttachmentDto,
  CreateUpdateContractDtoStatusEnum,
  SignaturePositionSchemaDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';

interface AttachmentViewerProps {
  attachmentId?: string;
  attachment: CreateUpdateAttachmentDto;
  showAttachmentViewer: boolean;
  withSignaturePosition: boolean;
  setSignaturePointer: (signature: SignaturePositionSchemaDto) => void;
  signaturePointer?: SignaturePositionSchemaDto | null;
  status: CreateUpdateContractDtoStatusEnum;
  saveSignaturePosition: () => void;
  authorizedOwner: boolean;
  activeContract: boolean;
}

export const AttachmentViewer: React.FC<AttachmentViewerProps> = props => {
  const [fileContent, setFileContent] = useState<Blob>();
  const [pdfFile, setPdfFile] = useState<boolean>(false);
  const [isPdfEncrypted, setIsPdfEncrypted] = useState<boolean>(false);

  const pok = usePokCore();
  const notifications = useNotifications();

  useEffect(() => {
    let file: Blob | undefined;
    if (props.attachmentId && !props.attachment?.fileContent) {
      pok.attachments
        .getFile(props.attachmentId)
        .then(response => {
          if (response.name.toLowerCase().endsWith('.pdf')) {
            setPdfFile(true);
            file = new Blob([Buffer.from(response.buffer as ArrayBuffer)]);
            setFileContent(file);
            pok.attachments
              .checkPdfIsEncrypted(props.attachmentId)
              .then(response => {
                setIsPdfEncrypted((response as unknown as string) === 'true');
              })
              .catch(error => {
                notifications.caughtError(error);
              });
          } else {
            setPdfFile(false);
            setFileContent(undefined);
          }
        })
        .catch(error => {
          notifications.caughtError(error);
        });
    } else {
      if (
        props.attachment?.fileContent &&
        props.attachment?.name?.toLowerCase().endsWith('.pdf')
      ) {
        setPdfFile(true);
        file = new Blob([Buffer.from(props.attachment.fileContent, 'base64')]);
        setFileContent(file);
        pok.attachments
          .checkPdfIsEncrypted(undefined, {
            fileContent: props.attachment?.fileContent,
          })
          .then(response => {
            setIsPdfEncrypted((response as unknown as string) === 'true');
          })
          .catch(error => {
            notifications.caughtError(error);
          });
      } else {
        setPdfFile(false);
        setFileContent(undefined);
      }
    }
    return () => {
      if (file) {
        setFileContent(undefined);
      }
    };
  }, [
    notifications,
    pok.attachments,
    props.attachment?.fileContent,
    props.attachment?.name,
    props.attachmentId,
  ]);

  if (fileContent && pdfFile && props.showAttachmentViewer) {
    return (
      <ReactPdf
        file={fileContent}
        setSignaturePosition={props.setSignaturePointer}
        signaturePosition={props.signaturePointer}
        status={props.status}
        saveSignaturePosition={props.saveSignaturePosition}
        authorizedOwner={props.authorizedOwner}
        isPdfEncrypted={isPdfEncrypted}
        withSignaturePosition={props.withSignaturePosition}
        activeContract={props.activeContract}
      />
    );
  }
  return null;
};
